import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { API } from '@unboared/react';
import { doc, onSnapshot } from 'firebase/firestore';

import { db, logEvent } from '~/config/firebase.config';
import { socketServerToken } from '~/config/global.config';
import { getRawSocket } from '~/utils/getRawSocket';
import { StaleLinkWatchdog, emitStaleLink } from '~/services/screenLink/staleLinkWatchdog';

/** Distinct de `ub::health-echo` (sonde de la boîte noire) : ne fausse pas sa mesure de latence. */
export const LINK_ECHO_MESSAGE = 'ub::link-echo';

/** Interrupteur à distance : `clients/{id}.features.staleLinkReconnect === false` coupe le veilleur
 * en direct (absent = actif). Couper tout le parc : `pilotage/veilleur_ecran.py off`. */
export const STALE_LINK_FLAG = 'staleLinkReconnect';

const BUDGET_KEY = 'ub:staleLink:forcedAt';

/** Délai aléatoire avant d'agir : jamais tous les écrans dans la même seconde. */
const JITTER_MAX_MS = 5000;

const HEALTH_TIMEOUT_MS = 3000;

function loadForcedAt(): number[] {
    try {
        const raw = JSON.parse(sessionStorage.getItem(BUDGET_KEY) || '[]');
        return Array.isArray(raw) ? raw.filter((x) => typeof x === 'number') : [];
    } catch {
        return [];
    }
}

function saveForcedAt(times: number[]) {
    try {
        sessionStorage.setItem(BUDGET_KEY, JSON.stringify(times.slice(-10)));
    } catch {
        /* stockage refusé : budget limité à la page, rien de grave */
    }
}

/** Le serveur répond-il en HTTP ? Réponse opaque (no-cors) : seule compte l'absence d'échec réseau.
 * `credentials: 'include'` : le polling engine.io part SANS cookies ; une requête avec cookies
 * vit dans une autre réserve de connexions du navigateur → elle ne peut pas réutiliser la
 * connexion morte qu'on cherche justement à diagnostiquer (sinon : « serveur muet », à tort). */
async function serverAnswers(managerUri: string | undefined): Promise<boolean> {
    if (!managerUri) return false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);
    try {
        const url = new URL('health', managerUri);
        url.searchParams.set('ub', 'link');
        await fetch(url.toString(), {
            mode: 'no-cors',
            cache: 'no-store',
            credentials: 'include',
            signal: controller.signal,
        });
        return true;
    } catch {
        return false;
    } finally {
        clearTimeout(timer);
    }
}

/**
 * Reconnexion rapide de l'écran quand sa connexion meurt sans bruit (détail et
 * garde-fous : services/screenLink/staleLinkWatchdog.ts). Vit toute la page TV
 * (enfant d'UnboaredGame, comme ScreenSessionRecovery), y compris hors partie —
 * la boîte noire, elle, n'existe que pendant un jeu. Serveur v2 uniquement : sur
 * v1 la lib fait une déconnexion locale complète à chaque coupure.
 */
export function ScreenStaleLinkReconnect({ clientID, socketServer }: { clientID: string; socketServer?: string }) {
    const [enabled, setEnabled] = useState(true);

    useEffect(
        () =>
            onSnapshot(
                doc(db, 'clients', clientID),
                (snap) => setEnabled(snap.data()?.features?.[STALE_LINK_FLAG] !== false),
                () => {
                    /* lecture refusée / hors ligne : on garde la dernière valeur */
                }
            ),
        [clientID]
    );

    // Même règle que supportsResume (makeUnboaredConfig) : reprise de session = v2 seulement.
    const active = enabled && socketServerToken(socketServer) === 'v2';

    useEffect(() => {
        if (!active) return undefined;

        // Session reprise depuis le dernier `connect` : avant `ready`, l'écho partirait
        // vers l'ancien socket et le veilleur casserait une reprise en cours.
        let readySinceConnect = Boolean(getRawSocket()?.connected && API.getDeviceID());
        // Démontage / interrupteur coupé pendant une vérification (jusqu'à ~8 s) : elle n'agit plus.
        let disposed = false;
        let lastIncoming: number | undefined;
        const onConnect = () => {
            readySinceConnect = false;
        };
        const onIncoming = () => {
            lastIncoming = performance.now();
        };
        // Relu à chaque tick : la lib peut recréer son socket (__reviveSocket).
        let wired: any;
        const unwire = () => {
            wired?.off('connect', onConnect);
            wired?.io?.off('packet', onIncoming);
            wired?.io?.off('ping', onIncoming);
        };
        const wire = () => {
            const wss = getRawSocket();
            if (!wss || wss === wired) return;
            unwire();
            wss.on('connect', onConnect);
            // Tout paquet reçu (messages des manettes, échos) et les pings du serveur.
            wss.io?.on('packet', onIncoming);
            wss.io?.on('ping', onIncoming);
            wired = wss;
        };
        wire();
        const unsubReady = API.onReady(() => {
            readySinceConnect = true;
        });

        const watchdog = new StaleLinkWatchdog({
            now: () => performance.now(),
            wallNow: () => Date.now(),
            // myDeviceID vaut '' tant que l'écran n'a pas sa place dans une session.
            isLinked: () => readySinceConnect && getRawSocket()?.connected === true && Boolean(API.getDeviceID()),
            isHidden: () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
            lastIncomingAt: () => lastIncoming,
            sendProbe: (id) => API.send(API.getDeviceID(), LINK_ECHO_MESSAGE, { id }),
            loadForcedAt,
            saveForcedAt,
            confirmAndReconnect: async () => {
                const wss = getRawSocket();
                await new Promise((resolve) => {
                    setTimeout(resolve, Math.floor(Math.random() * JITTER_MAX_MS));
                });
                const manager = wss?.io;
                // Serveur muet aussi en HTTP : c'est lui (ou tout le réseau) qui cale, pas
                // la connexion de cet écran — socket.io s'en chargera, sans tempête.
                if (disposed || !(await serverAnswers(manager?.uri))) return false;
                // Tout ce qui a pu changer pendant les await : démontage ou interrupteur
                // coupé, socket recréé (ScreenApp remonté : ne pas couper le neuf en pleine
                // reprise), socket déjà retombé ou en reprise, onglet caché, paquet reçu.
                if (
                    disposed ||
                    getRawSocket() !== wss ||
                    !readySinceConnect ||
                    wss?.connected !== true ||
                    document.visibilityState === 'hidden' ||
                    (lastIncoming !== undefined && performance.now() - lastIncoming < StaleLinkWatchdog.STALE_MS)
                ) {
                    return false;
                }
                // Même chemin qu'un « ping timeout » détecté par socket.io : le Manager
                // ferme, puis se reconnecte seul ; la lib reprend sa place (updateSID).
                // PAS engine.close() : il attend que le tampon d'envoi se vide, ce qui
                // n'arrive jamais sur une connexion morte.
                if (typeof manager?.onclose === 'function') {
                    manager.onclose('ping timeout');
                    return true;
                }
                Sentry.addBreadcrumb({ category: 'socket.io', level: 'warning', message: 'Manager.onclose missing' });
                return false;
            },
            onStale: ({ staleMs, acted, reason }) => {
                Sentry.addBreadcrumb({
                    category: 'socket.io',
                    level: 'warning',
                    message: acted ? 'Screen link stale: reconnect forced' : `Screen link stale: not acted (${reason})`,
                    data: { staleMs, acted, reason },
                });
                logEvent('screen_link_stale', clientID, {
                    clientID,
                    stale_ms: Math.round(staleMs),
                    acted: acted ? 1 : 0,
                    reason: reason ?? 'forced',
                });
                emitStaleLink({ staleMs, acted, reason });
            },
        });

        const unsubEcho = API.onMessage(LINK_ECHO_MESSAGE, (from: string, data?: { id?: number }) => {
            if (from === API.getDeviceID() && typeof data?.id === 'number') {
                watchdog.noteEcho(data.id);
            }
        });
        const timer = setInterval(() => {
            wire();
            watchdog.tick();
        }, StaleLinkWatchdog.TICK_MS);

        return () => {
            disposed = true;
            clearInterval(timer);
            unsubEcho?.();
            unsubReady?.();
            unwire();
        };
    }, [active, clientID]);

    return null;
}
